import React, { useState, useEffect, useCallback, useMemo } from "react";
import BusinessIcon from "@mui/icons-material/Business";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import BranchCard from "../components/branches/BranchCard";
import CreateBranchModal from "../components/branches/CreateBranchModal";
import UpdateBranchModal from "../components/branches/UpdateBranchModal";
import ViewBranchModal from "../components/branches/ViewBranchModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getBranches,
  getBranchById,
  deleteBranch,
  resetBranchServerStatus,
} from "../redux/actions/branchActions";
import { BranchSummaryDto } from "../shared/dtos/branch/BranchSummaryDto";
import { BranchResponseDto } from "../shared/dtos/branch/BranchResponseDto";
import { BRANCH_STATUS_OPTIONS } from "../shared/data/enumOptions";

const BranchesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- REDUX SELECTORS ---
  const {
    items: branches,
    item: branch,
    loadingFetchAll: loadingBranches,
    loadingFetchOne: loadingBranch,
    errorFetchAll: errorLoadingBranches,
    errorFetchOne: errorLoadingBranch,
    serverStatus,
    loadingCreate: loadingBranchCreate,
    errorCreate: errorCreatingBranch,
    loadingUpdate: loadingBranchUpdate,
    errorUpdate: errorUpdatingBranch,
    loadingDelete: loadingBranchDelete,
  } = useAppSelector((state) => state.branch);

  // --- LOCAL FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  // --- MODAL STATE ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedBranch, setSelectedBranch] = useState<
    BranchSummaryDto | BranchResponseDto | null
  >(null);

  const canCreate = hasPermission("branch:create");
  const canUpdate = hasPermission("branch:update");
  const canDelete = hasPermission("branch:delete");
  const canView = hasPermission("branch:read");

  const fetchBranches = useCallback((): void => {
    dispatch(getBranches());
  }, [dispatch]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // Instant Frontend Search Filtering
  const filteredBranches = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return branches.filter((b) => {
      const matchesStatus = statusFilter
        ? b.branchStatus === statusFilter
        : true;
      if (!matchesStatus) return false;

      if (!query) return true;

      const matchesName = b.name?.toLowerCase().includes(query);
      const matchesCode = (b as any).code?.toLowerCase().includes(query);
      const matchesCity = (b as any).city?.toLowerCase().includes(query);

      return Boolean(matchesName || matchesCode || matchesCity);
    });
  }, [branches, searchTerm, statusFilter]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingBranchCreate) setIsCreateModalOpen(false);
  }, [loadingBranchCreate]);

  const handleOpenUpdate = (
    targetBranch: BranchSummaryDto | BranchResponseDto,
  ): void => {
    dispatch(getBranchById(targetBranch.id));
    setSelectedBranch(targetBranch);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingBranchUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedBranch(null);
    }
  }, [loadingBranchUpdate]);

  const handleOpenView = (
    targetBranch: BranchSummaryDto | BranchResponseDto,
  ): void => {
    dispatch(getBranchById(targetBranch.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleOpenDelete = (
    targetBranch: BranchSummaryDto | BranchResponseDto,
  ): void => {
    setSelectedBranch(targetBranch);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingBranchDelete) {
      setIsDeleteModalOpen(false);
      setSelectedBranch(null);
    }
  }, [loadingBranchDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedBranch) {
      dispatch(deleteBranch(selectedBranch.id));
    }
  };

  // Watch Server Status
  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;
    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetBranchServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="School Branches"
      subtitle="Manage all campus locations and their details."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<BusinessIcon />}
            onClick={handleOpenCreate}
          >
            Add New Branch
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search branches by name, code, or city..."
      >
        <AppSelectFilter
          label="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={BRANCH_STATUS_OPTIONS}
          allLabel="All Statuses"
          minWidth={150}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingBranches}
        error={errorLoadingBranches}
        itemCount={filteredBranches.length}
        emptyMessage="No Branches Found"
        onRetry={fetchBranches}
      >
        {filteredBranches.map((b) => (
          <BranchCard
            key={b.id}
            branch={b}
            onView={canView ? () => handleOpenView(b) : undefined}
            onEdit={canUpdate ? () => handleOpenUpdate(b) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(b) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateBranchModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingBranchCreate}
          error={errorCreatingBranch}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedBranch && (
        <UpdateBranchModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          branch={
            (branch?.id === selectedBranch.id
              ? branch
              : selectedBranch) as BranchResponseDto
          }
          loading={loadingBranchUpdate || loadingBranch}
          error={errorUpdatingBranch || errorLoadingBranch}
        />
      )}

      {/* VIEW MODAL */}
      {isViewModalOpen && (
        <ViewBranchModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          branch={branch}
          loading={loadingBranch}
          error={errorLoadingBranch}
        />
      )}

      {/* DELETE DIALOG */}
      {isDeleteModalOpen && selectedBranch && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Branch"
          description={`Are you sure you want to delete the branch "${selectedBranch.name}"? This action cannot be undone.`}
        />
      )}
    </PageContainer>
  );
};

export default BranchesPage;
